'use client'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Stack, Divider } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSquareList as formIcon,
  faAngleLeft as previousIcon,
  faAngleRight as nextIcon,
  faFloppyDisk as saveIcon,
  faArrowDownLeftAndArrowUpRightToCenter as collapseIcon,
  faArrowUpRightAndArrowDownLeftFromCenter as expandIcon,
  faSpinnerThird, faRefresh
} from '@fortawesome/pro-regular-svg-icons'
import ParentCard from 'src/components/era-package/shared/ParentCard'
import BasicCard from 'src/components/era-package/shared/BasicCard'
import Button from 'src/components/era-package/forms/Button'
import Buttons from 'src/components/era-package/era/Buttons'
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection'
import SnackBar from 'src/components/era-package/era/SnackBar'
import { useTabbedForm } from 'src/contexts/era-package/TabbedFormContext'
import { useFormConfigContext } from './hooks/useFormConfig'
import AbiEntrySummaryApiUrls from '../../../apiUrls/AbiEntrySummaryApiUrls'
import pathMap from '../../../paths/pathMap'
import AbiQueryApiUrls from "../../../apiUrls/AbiQueryApiUrls";

const TabNavigation = () => {
  let tabs = useTabbedForm() || null

  if (!tabs) return <></>
  return (
    <>
      {tabs.activeTab > 0 && (
        <Button
          startIcon={<FontAwesomeIcon icon={previousIcon} width={10} />}
          onClick={() => {
            tabs.setActiveTab(tabs.activeTab - 1)
            document.documentElement.scrollTo({ top: 0, behavior: 'smooth' })
          }}
          title='Switch to previous tab'
        >
          Previous
        </Button>
      )}
      {!tabs.isLastTab && (
        <Button
          type='submit'
          variant='contained'
          endIcon={<FontAwesomeIcon icon={nextIcon} width={10} />}
          onClick={() => {
            tabs.setActiveTab(tabs.activeTab + 1)
            document.documentElement.scrollTo({ top: 0, behavior: 'smooth' })
          }}
          title='Switch to next tab'
        >
          Next
        </Button>
      )}
    </>
  )
}

// Internal component that renders the form content
const RenderFormContent = ({
  title,
  formSections,
  optionsMap = {},
  formData,
  setFormData,
  errors,
  setErrors,
  validateForm,
  submitLabel = 'Submit',
  onSubmit,
  hasUnsaved, // legacy
  setHasUnsaved, // legacy
  readOnly = false,
  isEntrySummary = false,
  enableUpload = true,
  basicForm = false,
  hideSave = false,
  allTabData, // legacy
  useRHF = false, // Internal prop to indicate RHF mode
  tabKey,
  displayRefresh = false,
  onRefresh,
  ...props
}) => {
  const [globalAccordionAction, setGlobalAccordionAction] = useState({
    type: null,
    timestamp: 0
  })
      const [showErrorSnackbar, setShowErrorSnackbar] = useState(false)
  const [showQuerySuccessSnackbar, setShowQuerySuccessSnackbar] = useState(false)
  const [loading, setLoading] = useState(false)
  const history = useHistory()

  // Get React Hook Form methods if available
  let rhfMethods = useFormConfigContext() || null
  let tabs = useTabbedForm() || null

  const { watch } = rhfMethods || {}

  useEffect(() => {
    const subscription = watch((values) => {
      if (tabs && useRHF) tabs.setFormData(values)
    })
  
    return () => subscription.unsubscribe()
  }, [watch])


  const handleSave = useCallback(async () => {
    let dataToSubmit

    if (useRHF && rhfMethods) {
      // Use React Hook Form data
      dataToSubmit = rhfMethods.getValues()
      // Reset dirty state
      rhfMethods.reset(dataToSubmit)
      if (!!tabs) tabs.setFormData(dataToSubmit)
    } else {
      // Use legacy data
      dataToSubmit = allTabData || formData
      if (setHasUnsaved) {
        setHasUnsaved(false)
      }
    }

    // Save data
    console.log('Saving:', dataToSubmit)
    if (typeof onSave === 'function') await onSave(dataToSubmit)
    console.log('Form saved:', dataToSubmit)
    alert('Form saved successfully!')
  }, [formData, allTabData, useRHF, rhfMethods, setHasUnsaved, tabs])

  const onSubmitError = useCallback(() => {
    let errorsToDisplay = errors
    if (useRHF) errorsToDisplay = rhfMethods.formState.errors
    console.log('Validation failed. Errors:', errorsToDisplay)
    // Show error snackbar when validation fails
    setShowErrorSnackbar(true)
  }, [errors, rhfMethods.formState, useRHF])

  const onSubmitSuccess = useCallback(async () => {
    let dataToSubmit

    // Use React Hook Form validation
    let isValid = true
    dataToSubmit = rhfMethods.getValues()

    // Run additional custom validation if provided
    if (typeof validateForm === 'function')
      isValid = await validateForm(dataToSubmit)

    if (isValid) {
      // Reset dirty state
      rhfMethods.reset(dataToSubmit, { keepValues: true })
      setShowErrorSnackbar(false)

      if (typeof onSubmit === 'function') await onSubmit(dataToSubmit)
      console.log('Form submitted:', dataToSubmit)
    } else {
      onSubmitError()
    }
  }, [validateForm, rhfMethods, onSubmit, onSubmitError])

  const handleSubmit = async (e, ...args) => {
    e.preventDefault()
    setLoading(true)
    if (useRHF) {
      console.log(rhfMethods)
      await rhfMethods.handleSubmit(onSubmitSuccess, onSubmitError)(...args)
    } else {
      let isValid = false
      let dataToSubmit

      // Use legacy validation
      if (typeof validateForm === 'function') isValid = await validateForm()
      else isValid = true

      if (isValid) {
        dataToSubmit = formData
        if (setHasUnsaved) {
          setHasUnsaved(false)
        }
        setShowErrorSnackbar(false)

        if (typeof onSubmit === 'function') await onSubmit(dataToSubmit)
        console.log('Form submitted:', dataToSubmit)
      } else onSubmitError()
    }
    setLoading(false)
  }

  // Handle snackbar close
    const handleSnackbarClose = useCallback(() => {
    setShowErrorSnackbar(false)
  }, [])

  const handleQuerySuccessSnackbarClose = useCallback(
    (e, reason) => {
      if (reason === 'timeout') {
        history.push(
          pathMap.ABI_ENTRY_SUMMARY_RESPONSE_VIEW.replace(
            ':id',
            showQuerySuccessSnackbar
          )
        )
        setShowQuerySuccessSnackbar(false)
      }
    },
    [history, showQuerySuccessSnackbar]
  )

  const Card = basicForm ? BasicCard : ParentCard

  const noButtons = useMemo(() => {
    if (!tabs && readOnly) return true
    if (hideSave && !submitLabel) return true
    return false
  }, [hideSave, submitLabel, tabs, readOnly])

  const onSave = async (dataToSave) => {
    console.log('start saving..')
    try {
      dataToSave.abiTransId = dataToSave.header.abiTransId
      dataToSave.cargo = convertCargoDatatoDTOForm(dataToSave.cargo)
      dataToSave.line = convertLineDatatoDTOForm(dataToSave.line)

      const response = await fetch(AbiEntrySummaryApiUrls.SAVE_ALL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(dataToSave) // Convert JS object to JSON string
      })

      if (!response.ok) {
        throw new Error('Failed to submit form')
      }

      const result = await response.json()
      console.log('Form saved:', dataToSave)
      console.log('status:' + result)
    } catch (error) {
      console.error('Error submitting form:', error)
    }
  }

  const handleQuerySubmit = useCallback(async (queryType) => {
    console.log(queryType)
    let dataToSubmit

    if (useRHF && rhfMethods) {
      // Use React Hook Form data
      dataToSubmit = rhfMethods.getValues()
      // Reset dirty state
      rhfMethods.reset(dataToSubmit)
      if (!!tabs) tabs.setFormData(dataToSubmit)
    } else {
      // Use legacy data
      dataToSubmit = allTabData || formData
      if (setHasUnsaved) {
        setHasUnsaved(false)
      }
    }

    // Save data
    console.log('Saving:', dataToSubmit)
    if (typeof onSave === 'function') await onQuery(dataToSubmit, queryType)
    console.log('Form saved:', dataToSubmit)
  }, [formData, allTabData, useRHF, rhfMethods, setHasUnsaved, tabs])

    const onQuery = async (dataToQuery, queryType) => {
    console.log('start query..')
    try {
      dataToQuery.abiTransId = dataToQuery.header.abiTransId
      dataToQuery.cargo = convertCargoDatatoDTOForm(dataToQuery.cargo)
      dataToQuery.line = convertLineDatatoDTOForm(dataToQuery.line)
      dataToQuery.queryType = queryType

      const response = await fetch(AbiEntrySummaryApiUrls.QUERY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(dataToQuery) // Convert JS object to JSON string
      })

      if (!response.ok) {
        throw new Error('Failed to submit form')
      }

            const result = await response.json()
      console.log('Form saved:', dataToQuery)
      console.log('status:' + result)

      // Show success snackbar and redirect after timeout
      setShowQuerySuccessSnackbar(dataToQuery.abiTransId)
    } catch (error) {
      console.error('Error submitting form:', error)
    }
  }

  function convertCargoDatatoDTOForm(input) {
    // UI to DTO form
    const grouped = {}

    for (const [key, value] of Object.entries(input)) {
      if (!key.includes('-')) continue // Skip fields without party code

      const [partyCode, field] = key.split('-')
      if (!grouped[partyCode]) {
        grouped[partyCode] = {
          partyLookup: partyCode,
          partyCode: partyCode
        }
      }
      grouped[partyCode][field] = value
    }

    return Object.values(grouped)
  }

  function convertLineDatatoDTOForm(input) {
    if (input?.items && Array.isArray(input.items)) {
      return input.items
    }
    return input
  }

  const fetchResponse = async (id) => {
    try {
      setLoading(true)
      const response = await fetch(`${AbiQueryApiUrls.GET_RESPONSE + `?id=${id}`}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      })

      if (!response.ok) {
        console.log('Error')
      }

      const json = await response.json()
      console.log(rhfMethods.getValues())
      tabs.setFormData({...rhfMethods.getValues(), response: json.data})
      rhfMethods.setValue("response", json.data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      console.log('Error')
    }
  }

  return (
    <Card
      title={
        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          gap={1}
        >
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            gap={1}
          >
            <FontAwesomeIcon icon={formIcon} />
            {title}
          </Box>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            gap={1}
          >
            <Button
              endIcon={<FontAwesomeIcon icon={collapseIcon} width={13} />}
              size='small'
              title='Collapse all the form sections'
              onClick={() =>
                setGlobalAccordionAction({
                  type: 'collapse',
                  timestamp: Date.now()
                })
              }
            >
              Collapse All
            </Button>
            <Button
              endIcon={<FontAwesomeIcon icon={expandIcon} width={13} />}
              size='small'
              title='Expand all the form sections'
              onClick={() =>
                setGlobalAccordionAction({
                  type: 'expand',
                  timestamp: Date.now()
                })
              }
            >
              Expand All
            </Button>
          </Box>
        </Box>
      }
    >
            {/* Error Snackbar */}
      <SnackBar
        title='Validation Error'
        type='error'
        open={showErrorSnackbar}
        onClose={handleSnackbarClose}
        duration={10000}
      >
        {!!tabs
          ? `Please fix the errors in the marked tabs before trying again.`
          : 'Please fix the errors on this form before trying again.'}
      </SnackBar>

      {/* Query Success Snackbar */}
      <SnackBar
        title='Query submitted successfully'
        type='success'
        open={!!showQuerySuccessSnackbar}
        onClose={handleQuerySuccessSnackbarClose}
        duration={3000}
      >
        Redirecting to Response Summary...
      </SnackBar>

      <Box noValidate>
        <Stack>
          {/* Form Sections */}
          {formSections.map((section, id) => (
            <RenderFormSection
              key={`form-accordion-${id}`}
              tabKey={tabKey}
              section={section}
              sectionIndex={id}
              optionsMap={optionsMap}
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              setHasUnsaved={setHasUnsaved}
              globalAccordionAction={globalAccordionAction}
              readOnly={readOnly || section.readOnly}
              enableUpload={enableUpload || section.enableUpload}
            />
          ))}

          {/* Action Buttons */}
          {!noButtons && (
            <>
              <Divider sx={{ my: 3 }} />

              <Buttons
                other={
                  <>
                    {!readOnly &&
                      !hideSave && (
                        <Button
                          startIcon={<FontAwesomeIcon icon={saveIcon} width={16} />}
                          onClick={handleSave}
                          disabled={
                            useRHF ? !rhfMethods?.formState?.isDirty : !hasUnsaved
                          }
                          title='Save data'
                        >
                          Save
                        </Button>
                      )
                    }
                    {
                      displayRefresh && (
                        <Button
                          variant='contained'
                          color='primary'
                          onClick={() => fetchResponse(tabs?.formData?.undefined?.response?.queryId)}
                          startIcon={
                            <FontAwesomeIcon
                              icon={faRefresh}
                              style={{ width: 16, height: 16 }}
                              spin={loading}
                            />
                          }
                        >
                          Refresh data
                        </Button>
                      )
                    }
                  </>
                }
                advance={
                  <>
                    <TabNavigation />
                                        {!readOnly &&
                      !!submitLabel &&
                      (!tabs || tabs.isLastTab) && (
                        <>
                          {isEntrySummary && (
                            <>
                              <Button
                                type='submit'
                                disabled={loading}
                                onClick={() => handleQuerySubmit("manifest")}
                              >
                                Manifest Query
                              </Button>
                              <Button
                                type='submit'
                                disabled={loading}
                                onClick={() => handleQuerySubmit("entry")}
                              >
                                Query Entry Summary
                              </Button>
                            </>
                          )}
                          <Button
                            type='submit'
                            variant='contained'
                            disabled={loading}
                            endIcon={
                              <FontAwesomeIcon
                                icon={loading ? faSpinnerThird : nextIcon}
                                width={10}
                                spin={loading}
                              />
                            }
                            onClick={handleSubmit}
                          >
                            {submitLabel}
                          </Button>
                        </>
                      )}
                  </>
                }
              />
            </>
          )}
        </Stack>
      </Box>
    </Card>
  )
}

// Main RenderForm component - now only handles legacy mode
// For React Hook Form, use RenderFormWithRHF component
const RenderForm = (props) => {
  return <RenderFormContent {...props} />
}

export default RenderForm
